import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HTTP_UNAUTHORIZED = 401;

export function useAdminAuth() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const tokenRef = useRef(null);

  const authApi = useCallback(() => {
    return axios.create({
      baseURL: API,
      headers: tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {},
    });
  }, [tokenRef]);

  const handleLogin = useCallback(async (email, password) => {
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      if (res.data.success) {
        tokenRef.current = res.data.token;
        setLoggedIn(true);
        setCurrentUser(res.data.user);
        toast.success("Logged in successfully.");
      }
    } catch {
      toast.error("Invalid credentials.");
    } finally {
      setLoginLoading(false);
    }
  }, [tokenRef, setLoggedIn, setCurrentUser, setLoginLoading]);

  const logout = useCallback(() => {
    setLoggedIn(false);
    setCurrentUser(null);
    tokenRef.current = null;
  }, [setLoggedIn, setCurrentUser, tokenRef]);

  return { loggedIn, setLoggedIn, currentUser, loginLoading, authApi, handleLogin, logout, tokenRef };
}

export function useAdminData(authApi, loggedIn, setLoggedIn, tokenRef) {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [clients, setClients] = useState([]);
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const api = authApi();
      const [s, c, n, cl, p, u, notif] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/contact"),
        api.get("/newsletter"),
        api.get("/certified-clients"),
        api.get("/blog?published="),
        api.get("/admin/users"),
        api.get("/admin/notifications"),
      ]);
      setStats(s.data);
      setContacts(c.data);
      setNewsletters(n.data);
      setClients(cl.data);
      setPosts(p.data);
      setUsers(u.data);
      setNotifications(notif.data);
    } catch (err) {
      if (err.response?.status === HTTP_UNAUTHORIZED) {
        toast.error("Session expired. Please login again.");
        setLoggedIn(false);
        tokenRef.current = null;
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  }, [authApi, setLoggedIn, tokenRef, setStats, setContacts, setNewsletters, setClients, setPosts, setUsers, setNotifications, setLoading]);

  return {
    stats, contacts, setContacts,
    newsletters, setNewsletters,
    clients, setClients,
    posts, setPosts,
    users, setUsers,
    notifications, setNotifications,
    loading, loadData,
  };
}
